import { useMemo, useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { usePages } from '../hooks/usePages';
import { useItem } from '../hooks/useItem';
import { useKeyboardNav } from '../hooks/useKeyboardNav';
import { useTags } from '../hooks/useTags';
import { itemsApi, downloadsApi } from '../api/items';
import type { MangaItem, Tag, DownloadShelfItem } from '../types';
import {
  TAG_GROUPS,
  type TagGroupKey,
  getTagGroup,
  getTagLabel,
  getTagColorClass,
} from '../utils/tagUtils';

type ThumbnailSize = 'small' | 'medium' | 'large';

export default function ViewerPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [showHelp, setShowHelp] = useState(true);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isPageListOpen, setIsPageListOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [thumbnailSize, setThumbnailSize] = useState<ThumbnailSize>('small');
  const [isMobile, setIsMobile] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [editingTags, setEditingTags] = useState<Tag[]>([]);
  const [editingRating, setEditingRating] = useState(0);
  const [tagStatus, setTagStatus] = useState<string | null>(null);
  const [isTagSaving, setIsTagSaving] = useState(false);
  const [isRatingSaving, setIsRatingSaving] = useState(false);
  const [editingTitle, setEditingTitle] = useState('');
  const [isTitleSaving, setIsTitleSaving] = useState(false);
  const [editingAuthorReading, setEditingAuthorReading] = useState('');
  const [isAuthorReadingSaving, setIsAuthorReadingSaving] = useState(false);
  const [editingIsInactive, setEditingIsInactive] = useState(false);
  const [isInactiveSaving, setIsInactiveSaving] = useState(false);
  const [isThumbnailSaving, setIsThumbnailSaving] = useState(false);
  const [isCbzExporting, setIsCbzExporting] = useState(false);
  const [cbzStatus, setCbzStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [copyFilenamesStatus, setCopyFilenamesStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [shelfItems, setShelfItems] = useState<DownloadShelfItem[]>([]);
  const [isShelfLoading, setIsShelfLoading] = useState(false);
  const { data, isLoading, error } = usePages(id!);
  const { data: itemData, error: itemError } = useItem(id!);
  const { data: tagsData } = useTags();

  const groupedTags = useMemo(() => {
    const groups: Record<TagGroupKey, Tag[]> = {
      style: [],
      mood: [],
      other: [],
    };

    tagsData?.tags.forEach((tag) => {
      // Only include tags with use_count > 0 (hide unused tags)
      if ((tag.use_count ?? 0) <= 0) return;
      groups[getTagGroup(tag.name)].push(tag);
    });

    (Object.keys(groups) as TagGroupKey[]).forEach((key) => {
      groups[key].sort((a, b) => a.name.localeCompare(b.name));
    });

    return groups;
  }, [tagsData]);

  // PCヘッダー用: タグをstyle→mood→otherの順にソート
  const sortedHeaderTags = useMemo(() => {
    if (!itemData?.tags) return [];
    const order: Record<TagGroupKey, number> = { style: 0, mood: 1, other: 2 };
    return [...itemData.tags].sort(
      (a, b) => order[getTagGroup(a.name)] - order[getTagGroup(b.name)]
    );
  }, [itemData?.tags]);

  // 初回表示時に5秒後にヘルプを非表示
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowHelp(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (itemData) {
      setEditingTags(itemData.tags ?? []);
      setEditingRating(itemData.rating ?? 0);
      setEditingTitle(itemData.title ?? '');
      setEditingAuthorReading(itemData.author_reading ?? '');
      setEditingIsInactive(itemData.is_inactive ?? false);
    }
  }, [itemData]);

  // 編集パネルを開いたときにダウンロードシェルフをロード
  useEffect(() => {
    if (isEditOpen && id) {
      loadShelf();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditOpen, id]);

  // マウント時に閲覧履歴を記録し、itemsキャッシュを無効化（履歴ページの即時反映のため）
  useEffect(() => {
    if (!id) return;
    itemsApi.recordHistory(id)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['items'] });
      })
      .catch(() => {});
  }, [id]);

  const totalPages = data?.pages.length || 0;

  const toggleEdit = () => setIsEditOpen((prev) => !prev);
  const togglePageList = () => setIsPageListOpen((prev) => !prev);

  useKeyboardNav(
    currentPage,
    totalPages,
    setCurrentPage,
    toggleEdit,
    togglePageList,
    isPageListOpen,
    setThumbnailSize
  );

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleImageAreaClick = (e: React.MouseEvent) => {
    if (isMobile) return; // モバイルはタッチイベントで処理
    if (e.clientX < window.innerWidth / 2) {
      handleNextPage(); // 左半分 → 次ページ（日本語漫画: 左が「進む」）
    } else {
      handlePrevPage(); // 右半分 → 前ページ
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/');
  };

  // スワイプナビゲーション（モバイル用）
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const longPressTimerRef = useRef<number | null>(null);
  const longPressTriggeredRef = useRef(false);
  // ページ一覧のサムネイルボタンを全件管理するMap
  const pageThumbRefsMap = useRef<Map<number, HTMLButtonElement>>(new Map());
  // ページ一覧のスクロールコンテナ・sticky ヘッダー
  const pageListContainerRef = useRef<HTMLDivElement | null>(null);
  const pageListHeaderRef = useRef<HTMLDivElement | null>(null);
  // サイズ変更後にスクロールするターゲットページ番号
  const scrollTargetPageRef = useRef<number | null>(null);

  // ページ一覧を開いたとき: 現在のページが中央付近に来るようスクロール
  useEffect(() => {
    if (isPageListOpen) {
      setTimeout(() => {
        pageThumbRefsMap.current.get(currentPage)?.scrollIntoView({ block: 'center', behavior: 'instant' });
      }, 0);
    }
  }, [isPageListOpen]);

  // サムネイルサイズ変更後: 変更前に見えていた先頭ページを表示領域に戻す
  useEffect(() => {
    if (!isPageListOpen || scrollTargetPageRef.current === null) return;
    const targetPage = scrollTargetPageRef.current;
    scrollTargetPageRef.current = null;
    setTimeout(() => {
      pageThumbRefsMap.current.get(targetPage)?.scrollIntoView({ block: 'center', behavior: 'instant' });
    }, 0);
  }, [thumbnailSize]);

  // サイズ変更前に「表示領域の中央に最も近いページ」を記録してからサイズを変更する
  const handleThumbnailSizeChange = (size: ThumbnailSize) => {
    if (size === thumbnailSize) return;
    const header = pageListHeaderRef.current;
    const container = pageListContainerRef.current;
    const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
    const containerBottom = container ? container.getBoundingClientRect().bottom : window.innerHeight;
    const viewCenter = (headerBottom + containerBottom) / 2;

    let centerPage: number | null = null;
    let minDist = Infinity;
    for (const [pageNum, el] of pageThumbRefsMap.current) {
      const rect = el.getBoundingClientRect();
      // sticky ヘッダーより下かつスクロール領域内に少しでも見えているボタンが対象
      if (rect.bottom > headerBottom && rect.top < containerBottom) {
        const elCenter = (rect.top + rect.bottom) / 2;
        const dist = Math.abs(elCenter - viewCenter);
        if (dist < minDist) {
          minDist = dist;
          centerPage = pageNum;
        }
      }
    }
    scrollTargetPageRef.current = centerPage;
    setThumbnailSize(size);
  };
  const SWIPE_THRESHOLD = 50; // px
  const LONG_PRESS_DELAY = 600; // ms

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleMediaChange = (event: MediaQueryListEvent | MediaQueryList) => {
      setIsMobile(event.matches);
    };

    handleMediaChange(mediaQuery);
    const listener = (event: MediaQueryListEvent) => handleMediaChange(event);
    mediaQuery.addEventListener('change', listener);
    return () => mediaQuery.removeEventListener('change', listener);
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (isMobileMenuOpen) return;
    const touch = e.touches[0];
    setTouchStartY(touch.clientY);
    setTouchStartX(touch.clientX);
    longPressTriggeredRef.current = false;
    if (isMobile) {
      if (longPressTimerRef.current !== null) {
        window.clearTimeout(longPressTimerRef.current);
      }
      longPressTimerRef.current = window.setTimeout(() => {
        longPressTriggeredRef.current = true;
        window.getSelection()?.removeAllRanges();
        setIsMobileMenuOpen(true);
      }, LONG_PRESS_DELAY);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    if (longPressTriggeredRef.current) {
      setTouchStartY(null);
      setTouchStartX(null);
      return;
    }
    if (touchStartY === null) return;
    const endY = e.changedTouches[0].clientY;
    const deltaY = touchStartY - endY;
    if (Math.abs(deltaY) < SWIPE_THRESHOLD) {
      // スワイプなし → タップ: 上半分タップ=前ページ、下半分タップ=次ページ
      if (touchStartY < window.innerHeight / 2) {
        handlePrevPage();
      } else {
        handleNextPage();
      }
    } else if (deltaY > 0) {
      handleNextPage(); // 上スワイプ → 次ページ
    } else {
      handlePrevPage(); // 下スワイプ → 前ページ
    }
    setTouchStartY(null);
    setTouchStartX(null);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartY === null || touchStartX === null) return;
    const touch = e.touches[0];
    const deltaY = Math.abs(touch.clientY - touchStartY);
    const deltaX = Math.abs(touch.clientX - touchStartX);
    if (deltaY > 10 || deltaX > 10) {
      if (longPressTimerRef.current !== null) {
        window.clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    }
  };

  const submitTags = async (names: string[]) => {
    if (!id) return;
    const cleaned = names.map((name) => name.trim()).filter(Boolean);
    if (cleaned.length === 0) return;

    const existing = new Set(editingTags.map((tag) => tag.name));
    const toAdd = cleaned.filter((name) => !existing.has(name));
    if (toAdd.length === 0) return;

    setIsTagSaving(true);
    setTagStatus(null);
    try {
      const response = await itemsApi.addTags(id, toAdd);
      setEditingTags((prev) => {
        const byName = new Map(prev.map((tag) => [tag.name, tag]));
        response.added_tags.forEach((tag) => {
          byName.set(tag.name, tag);
        });
        return Array.from(byName.values());
      });
      setTagInput('');
    } catch (err) {
      setTagStatus(err instanceof Error ? err.message : 'タグの更新に失敗しました');
    } finally {
      setIsTagSaving(false);
    }
  };

  const handleTagInputSubmit = () => {
    const parts = tagInput.split(',').map((name) => name.trim());
    submitTags(parts);
  };

  const handleRemoveTag = async (tag: Tag) => {
    if (!id) return;
    setIsTagSaving(true);
    setTagStatus(null);
    try {
      await itemsApi.removeTag(id, tag.id);
      setEditingTags((prev) => prev.filter((t) => t.id !== tag.id));
    } catch (err) {
      setTagStatus(err instanceof Error ? err.message : 'タグの削除に失敗しました');
    } finally {
      setIsTagSaving(false);
    }
  };

  const handleRatingChange = async (rating: number) => {
    if (!id) return;
    setIsRatingSaving(true);
    try {
      await itemsApi.updateRating(id, rating);
      setEditingRating(rating);
    } catch (err) {
      setTagStatus(err instanceof Error ? err.message : '評価の更新に失敗しました');
    } finally {
      setIsRatingSaving(false);
    }
  };

  const handleTitleChange = async () => {
    if (!id) return;
    const trimmed = editingTitle.trim();
    if (!trimmed) return;
    setIsTitleSaving(true);
    setTagStatus(null);
    try {
      await itemsApi.updateTitle(id, trimmed);
    } catch (err) {
      setTagStatus(
        err instanceof Error ? err.message : 'タイトルの更新に失敗しました'
      );
    } finally {
      setIsTitleSaving(false);
    }
  };

  const handleAuthorReadingChange = async () => {
    if (!id) return;
    setIsAuthorReadingSaving(true);
    setTagStatus(null);
    try {
      await itemsApi.updateAuthorReading(id, editingAuthorReading.trim());
    } catch (err) {
      setTagStatus(
        err instanceof Error ? err.message : '作者ふりがなの更新に失敗しました'
      );
    } finally {
      setIsAuthorReadingSaving(false);
    }
  };

  const handleInactiveChange = async (nextValue: boolean) => {
    if (!id) return;
    setIsInactiveSaving(true);
    setTagStatus(null);
    const prevValue = editingIsInactive;
    setEditingIsInactive(nextValue);
    try {
      await itemsApi.updateInactive(id, nextValue);
    } catch (err) {
      setEditingIsInactive(prevValue);
      setTagStatus(
        err instanceof Error ? err.message : 'inactiveの更新に失敗しました'
      );
    } finally {
      setIsInactiveSaving(false);
    }
  };

  const handleSetThumbnail = async (s3Key: string) => {
    if (!id) return;
    setIsThumbnailSaving(true);
    setTagStatus(null);
    try {
      await itemsApi.updateThumbnail(id, s3Key);
      // API成功後、キャッシュを即時更新してチェック状態を反映
      queryClient.setQueryData(['item', id], (old: MangaItem | undefined) =>
        old ? { ...old, thumbnail_s3_key: s3Key } : old
      );
    } catch (err) {
      setTagStatus(
        err instanceof Error ? err.message : 'サムネイルの更新に失敗しました'
      );
    } finally {
      setIsThumbnailSaving(false);
    }
  };

  const handleCbzExport = async () => {
    if (!id || isCbzExporting || !itemData) return;
    setIsCbzExporting(true);
    setCbzStatus(null);
    try {
      // "[作者名] タイトル.cbz" 形式でファイル名を組み立てる
      const author = itemData.author_reading || itemData.author || 'Unknown';
      const filename = `[${author}] ${itemData.title}.cbz`;
      await itemsApi.exportCbz(id, filename);
      setCbzStatus({ type: 'success', message: '生成を開始しました。数分後にシェルフを更新してください' });
    } catch (err) {
      const message = err instanceof Error ? err.message : '生成に失敗しました';
      setCbzStatus({ type: 'error', message: `生成に失敗しました: ${message}` });
    } finally {
      setIsCbzExporting(false);
    }
  };

  const handleCopyFilenames = async () => {
    if (!data?.pages || !id) return;
    const lines = [
      id,
      data.title ?? '',
      ...data.pages.map(page => page.s3_key.split('/').pop() ?? ''),
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopyFilenamesStatus({ type: 'success', message: 'コピーしました' });
    } catch {
      setCopyFilenamesStatus({ type: 'error', message: 'コピーに失敗しました' });
    }
    setTimeout(() => setCopyFilenamesStatus(null), 3000);
  };

  const loadShelf = async () => {
    if (isShelfLoading) return;
    setIsShelfLoading(true);
    try {
      const response = await downloadsApi.listShelf();
      setShelfItems(response.items);
    } catch {
      // エラーは無視（シェルフ更新は補助機能のため）
    } finally {
      setIsShelfLoading(false);
    }
  };

  const handleDeleteShelfItem = async (filename: string) => {
    try {
      await downloadsApi.deleteShelfItem(filename);
      setShelfItems(prev => prev.filter(item => item.filename !== filename));
    } catch {
      // エラーは無視
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <div className="text-xl text-white">Loading...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <div className="text-xl text-red-500">
          Error: {error instanceof Error ? error.message : 'Failed to load manga'}
        </div>
      </div>
    );
  }

  const currentPageData = data.pages[currentPage - 1];

  const effectiveThumbnailSize = isMobile ? 'small' : thumbnailSize;

  const getThumbnailSizeClass = () => {
    switch (effectiveThumbnailSize) {
      case 'small':
        return 'w-24';
      case 'medium':
        return 'w-40';
      case 'large':
        return 'w-72';
      default:
        return 'w-40';
    }
  };

  const handlePageSelect = (page: number) => {
    setCurrentPage(page);
    setIsPageListOpen(false);
  };

  return (
    <div className="relative h-[100dvh] bg-black">
      {/* Top Toolbar (PCのみ表示) */}
      <div className="hidden md:block absolute top-0 left-0 right-0 bg-black/80 text-white z-10 px-4 py-2 md:py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          {/* Backボタン: PCのみ */}
          <button
            onClick={handleBack}
            className="hidden md:block px-2 py-2 bg-gray-700 hover:bg-gray-600 rounded"
          >
            ←
          </button>

          {/* タイトル: PCのみ */}
          <div className="hidden md:block text-left mx-4">
            {/* 1行目: タイトル / 作者名 */}
            <h2 className="font-bold text-sm leading-tight">
              {data.title}
              <span className="mx-1.5 text-gray-500 font-normal">/</span>
              <span className="font-normal text-gray-300">{data.author}</span>
            </h2>
            {/* 2行目: レーティング + タグ（style→mood→other順） */}
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              <div className="flex items-center gap-0.5 mr-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <span
                    key={i}
                    className={`text-xs ${i <= (itemData?.rating ?? 0) ? 'text-yellow-400' : 'text-gray-600'}`}
                  >
                    ★
                  </span>
                ))}
              </div>
              {sortedHeaderTags.map((tag) => (
                <span
                  key={tag.id}
                  className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] ${getTagColorClass(tag.name)}`}
                >
                  {getTagLabel(tag.name)}
                </span>
              ))}
            </div>
          </div>

          {/* 右側コントロール（常に表示）*/}
          <div className="flex items-center gap-3 ml-auto">
            <button
              onClick={() => setIsEditOpen(true)}
              className="p-2 bg-white/10 hover:bg-white/20 rounded text-sm"
            >
              ✏️
            </button>
            <button
              onClick={togglePageList}
              className="px-2 py-1 bg-white/20 hover:bg-white/30 rounded text-white font-semibold cursor-pointer text-sm"
            >
              <span className="md:hidden">{currentPage}</span>
              <span className="hidden md:inline">{currentPage} / {totalPages}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Image Viewer */}
      <div
        className="flex items-center justify-center h-full pt-0 md:pt-20 md:cursor-pointer select-none"
        style={{ WebkitTouchCallout: 'none' } as React.CSSProperties}
        onClick={handleImageAreaClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onContextMenu={(event) => event.preventDefault()}
      >
        {currentPageData ? (
          <img
            src={currentPageData.url}
            alt={`Page ${currentPage}`}
            className="max-h-full max-w-full object-contain select-none pointer-events-none"
            draggable={false}
            onContextMenu={(event) => event.preventDefault()}
          />
        ) : (
          <div className="text-white">No image available</div>
        )}
      </div>


      {/* Keyboard Shortcuts Hint - 初回5秒間のみ表示 */}
      {showHelp && (
        <>
          <div className="hidden md:block absolute bottom-4 right-4 text-white text-xs bg-black/70 px-3 py-2 rounded transition-opacity duration-500">
            ← : 次へ | → : 前へ | P: ページ一覧 | E: 編集 | Esc: 戻る
          </div>
          <div className="md:hidden absolute bottom-4 right-4 text-white text-xs bg-black/70 px-3 py-2 rounded transition-opacity duration-500 whitespace-nowrap">
            スワイプ/上下タップ: ページ移動 | 長プレス: メニュー
          </div>
        </>
      )}

      {/* Page List Modal */}
      {isPageListOpen && (
        <div ref={pageListContainerRef} className="absolute inset-0 z-20 bg-black/90 overflow-y-auto">
          <div className="min-h-full p-6">
            <div className="max-w-7xl mx-auto">
              {/* Header */}
              <div ref={pageListHeaderRef} className="flex items-start justify-between mb-6 sticky top-0 bg-black/80 py-4 z-10">
                <div className="flex items-center gap-4">
                  <div>
                    <h2 className="text-l font-bold text-white">
                      ページ一覧 ({totalPages}pages)
                    </h2>
                    <p className="text-sm text-gray-400 mt-0.5">{data.title}</p>
                  </div>
                  {/* サイズ切替: PCのみ表示 */}
                  <div className="hidden md:flex items-center gap-2">
                    <span className="text-sm text-gray-400">サイズ:</span>
                    <button
                      onClick={() => handleThumbnailSizeChange('small')}
                      className={`px-3 py-1 rounded text-sm ${
                        thumbnailSize === 'small'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      小
                    </button>
                    <button
                      onClick={() => handleThumbnailSizeChange('medium')}
                      className={`px-3 py-1 rounded text-sm ${
                        thumbnailSize === 'medium'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      中
                    </button>
                    <button
                      onClick={() => handleThumbnailSizeChange('large')}
                      className={`px-3 py-1 rounded text-sm ${
                        thumbnailSize === 'large'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      大
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => setIsPageListOpen(false)}
                  className="px-2 py-1 bg-white/20 hover:bg-white/30 rounded text-white font-semibold text-sm"
                  aria-label="閉じる"
                >
                  ✕
                </button>
              </div>

              {/* Thumbnail Grid */}
              <div className="grid gap-4 justify-center" style={{
                gridTemplateColumns: `repeat(auto-fill, minmax(${effectiveThumbnailSize === 'small' ? '6rem' : effectiveThumbnailSize === 'medium' ? '10rem' : '18rem'}, 1fr))`,
                direction: 'rtl'
              }}>
                {data.pages.map((page) => (
                  <button
                    key={page.page}
                    ref={(el) => {
                      if (el) pageThumbRefsMap.current.set(page.page, el);
                      else pageThumbRefsMap.current.delete(page.page);
                    }}
                    onClick={() => handlePageSelect(page.page)}
                    className={`relative group overflow-hidden rounded border-2 transition-all ${
                      currentPage === page.page
                        ? 'border-blue-500 ring-2 ring-blue-500'
                        : 'border-gray-700 hover:border-gray-500'
                    }`}
                  >
                    <img
                      src={page.url}
                      alt={`Page ${page.page}`}
                      className={`${getThumbnailSizeClass()} object-cover aspect-[3/4]`}
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white font-bold text-lg">
                        {page.page}
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-black/80 text-white text-xs py-1 text-center">
                      {page.page}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {isEditOpen && (
        <div className="absolute inset-0 z-20 bg-black/60 flex justify-end" onClick={() => setIsEditOpen(false)}>
          <div className="h-full w-full max-w-md bg-white text-gray-900 shadow-xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white sticky top-0 z-10">
              <div>
                <div className="text-lg font-semibold">✏️編集</div>
                <div className="text-xs text-gray-500">
                  {data.title} / {data.author}
                </div>
              </div>
              <button
                onClick={() => setIsEditOpen(false)}
                className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-gray-700 font-semibold text-sm"
                aria-label="閉じる"
              >
                ✕
              </button>
            </div>

            <div className="p-4 space-y-5 overflow-y-auto">
              {itemError && (
                <div className="text-xs text-red-600">
                  作品情報の取得に失敗しました
                </div>
              )}

              {/* 0. 評価 */}
              <div>
                <div className="text-sm font-semibold mb-2">評価</div>
                <div className="flex flex-wrap gap-2">
                  {[0, 1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      onClick={() => handleRatingChange(rating)}
                      disabled={isRatingSaving}
                      className={`px-3 py-2 rounded border text-sm transition ${
                        rating === editingRating
                          ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      } disabled:opacity-50`}
                    >
                      ★{rating}
                    </button>
                  ))}
                </div>
              </div>

              {/* 1. 選択済みタグ */}
              <div>
                <div className="text-sm font-semibold mb-2">選択済みタグ</div>
                <div className="flex flex-wrap gap-2">
                  {editingTags.length === 0 && (
                    <span className="text-xs text-gray-400">未選択</span>
                  )}
                  {editingTags.map((tag) => (
                    <button
                      key={tag.id}
                      onClick={() => handleRemoveTag(tag)}
                      disabled={isTagSaving}
                      className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs hover:opacity-80 disabled:opacity-50 ${getTagColorClass(tag.name)}`}
                    >
                      {getTagLabel(tag.name)}
                      <span className="opacity-60">×</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* 2. タグを追加 */}
              <div>
                <div className="text-sm font-semibold mb-2">タグを追加</div>
                <div className="flex gap-2">
                  <input
                    value={tagInput}
                    onChange={(event) => setTagInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleTagInputSubmit();
                      }
                    }}
                    placeholder="例: style.kawaii, mood.innocent"
                    className="flex-1 rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                  <button
                    onClick={handleTagInputSubmit}
                    disabled={isTagSaving}
                    className="px-3 py-2 rounded bg-gray-800 text-white text-sm disabled:opacity-50"
                  >
                    追加
                  </button>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  カンマ区切りで複数入力できます
                </div>
              </div>

              {/* 3. タグ候補 */}
              <div>
                <div className="text-sm font-semibold mb-2">タグ候補</div>
                <div className="grid gap-3">
                  {TAG_GROUPS.map((group) => (
                    <div key={group.key}>
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                        {group.label}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {groupedTags[group.key]?.map((tag) => {
                          const active = editingTags.some(
                            (selected) => selected.name === tag.name
                          );
                          return (
                            <button
                              key={tag.id}
                              onClick={() => submitTags([tag.name])}
                              disabled={active || isTagSaving}
                              className={`px-3 py-1 rounded-full border text-xs transition hover:opacity-80 disabled:opacity-40 ${getTagColorClass(tag.name)}`}
                            >
                              {getTagLabel(tag.name)}
                            </button>
                          );
                        })}
                        {groupedTags[group.key]?.length === 0 && (
                          <span className="text-xs text-gray-400">No tags</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 4. サムネイル */}
              {currentPageData && (
                <div>
                  <div className="text-sm font-semibold mb-2">サムネイル</div>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={
                        !!itemData?.thumbnail_s3_key &&
                        itemData.thumbnail_s3_key === currentPageData.s3_key
                      }
                      onChange={(event) => {
                        if (event.target.checked) {
                          handleSetThumbnail(currentPageData.s3_key);
                        }
                      }}
                      disabled={isThumbnailSaving}
                    />
                    このページをサムネイルに指定
                    {isThumbnailSaving && (
                      <span className="text-xs text-gray-400 ml-1">保存中...</span>
                    )}
                  </label>
                  <div className="text-xs text-gray-500 mt-1">
                    ページ {currentPage} / {totalPages}
                  </div>
                </div>
              )}

              {/* 5. タイトル */}
              <div>
                <div className="text-sm font-semibold mb-2">タイトル</div>
                <div className="flex gap-2">
                  <input
                    value={editingTitle}
                    onChange={(event) => setEditingTitle(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleTitleChange();
                      }
                    }}
                    placeholder="タイトルを入力"
                    className="flex-1 rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                  <button
                    onClick={handleTitleChange}
                    disabled={isTitleSaving}
                    className="px-3 py-2 rounded bg-gray-800 text-white text-sm disabled:opacity-50"
                  >
                    {isTitleSaving ? '保存中...' : '保存'}
                  </button>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  シリーズ化する場合は末尾に巻数（例: 1）を追加してください
                </div>
              </div>

              {/* 6. 作者ふりがな */}
              <div>
                <div className="text-sm font-semibold mb-2">作者ふりがな</div>
                <div className="flex gap-2">
                  <input
                    value={editingAuthorReading}
                    onChange={(event) => setEditingAuthorReading(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleAuthorReadingChange();
                      }
                    }}
                    placeholder="例: やまだたろう"
                    className="flex-1 rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                  <button
                    onClick={handleAuthorReadingChange}
                    disabled={isAuthorReadingSaving}
                    className="px-3 py-2 rounded bg-gray-800 text-white text-sm disabled:opacity-50"
                  >
                    保存
                  </button>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  フィルター検索に使用されます
                </div>
              </div>

              {/* 7. inactive */}
              <div>
                <div className="text-sm font-semibold mb-2">inactive</div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={editingIsInactive}
                      onChange={(event) => handleInactiveChange(event.target.checked)}
                      disabled={isInactiveSaving}
                    />
                    非アクティブにする
                  </label>
                </div>
              </div>

              {/* CBZエクスポート */}
              <div>
                <div className="text-sm font-semibold mb-2">エクスポート</div>
                <button
                  onClick={handleCbzExport}
                  disabled={isCbzExporting}
                  className="w-full px-3 py-2 rounded border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 text-left"
                >
                  {isCbzExporting ? '生成中...' : '📖 エクスポート'}
                </button>
                {cbzStatus && (
                  <div className={`text-xs mt-1 ${cbzStatus.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                    {cbzStatus.type === 'success' ? '✓ ' : '✗ '}{cbzStatus.message}
                  </div>
                )}
                <button
                  onClick={handleCopyFilenames}
                  className="w-full px-3 py-2 rounded border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 text-left mt-2"
                >
                  📋 ページ順ファイル名をコピー
                </button>
                {copyFilenamesStatus && (
                  <div className={`text-xs mt-1 ${copyFilenamesStatus.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                    {copyFilenamesStatus.type === 'success' ? '✓ ' : '✗ '}{copyFilenamesStatus.message}
                  </div>
                )}
                {/* シェルフ */}
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500">シェルフ</span>
                    <button
                      onClick={loadShelf}
                      disabled={isShelfLoading}
                      className="text-xs text-blue-500 hover:text-blue-700 disabled:opacity-50"
                    >
                      {isShelfLoading ? '更新中...' : '更新'}
                    </button>
                  </div>
                  {shelfItems.length > 0 ? (
                    <div className="space-y-1">
                      {shelfItems.map(item => (
                        <div key={item.filename} className="flex items-center justify-between text-xs">
                          <a
                            href={item.download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 underline truncate max-w-[160px]"
                            title={item.filename}
                          >
                            ⬇ {item.filename} ({item.file_size_mb}MB)
                          </a>
                          <button
                            onClick={() => handleDeleteShelfItem(item.filename)}
                            className="text-red-500 hover:text-red-700 ml-2 shrink-0"
                          >
                            削除
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400">なし</div>
                  )}
                </div>
              </div>

              {tagStatus && (
                <div className="text-xs text-red-600">{tagStatus}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Long-Press Menu */}
      {isMobileMenuOpen && (
        <div
          className="absolute inset-0 z-30 bg-black/60 flex items-end justify-center p-4 md:hidden select-none"
          style={{ WebkitTouchCallout: 'none' } as React.CSSProperties}
        >
          <div className="w-full max-w-sm rounded-2xl bg-white text-gray-900 shadow-xl">
            <div className="px-4 pt-4 pb-3 border-b border-gray-200">
              <div className="text-sm font-semibold">{data.title}</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {data.author} ({currentPage} / {totalPages})
              </div>
            </div>
            <div className="p-3 grid gap-2">
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setIsPageListOpen(true);
                }}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                ページ一覧
              </button>
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setIsEditOpen(true);
                }}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                編集
              </button>
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  handleBack();
                }}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                作品一覧
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-full rounded-lg bg-gray-900 px-3 py-2 text-sm text-white"
              >
                キャンセル
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
